// src/services/AuthService.ts
import {
  Session,
  AuthError,
  SupabaseClient,
  PostgrestError,
} from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";
import { UserSession } from "../types/database.enhanced";

// Custom error type that can be either AuthError or PostgrestError
export type AuthServiceError = AuthError | PostgrestError | Error;

export interface AuthResponse<T = any> {
  data: T | null;
  error: AuthServiceError | null;
}

interface AuthOperation {
  operation: string;
  duration: number;
  success: boolean;
  error?: string;
  timestamp: Date;
}

export class AuthService {
  private static instance: AuthService;
  private client: SupabaseClient;
  private operations: AuthOperation[] = [];
  private readonly MAX_OPERATIONS = 1000;

  private constructor() {
    this.client = supabase.getClient();
  }

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  // Session Management
  async getSession(): Promise<AuthResponse<Session>> {
    try {
      const {
        data: { session },
        error,
      } = await this.client.auth.getSession();

      if (error) {
        this.recordOperation("get_session", false, error.message);
        return { data: null, error };
      }

      this.recordOperation("get_session", true);
      return { data: session, error: null };
    } catch (error) {
      const errorObj =
        error instanceof Error ? error : new Error(String(error));
      this.recordOperation("get_session", false, errorObj.message);
      return { data: null, error: errorObj };
    }
  }

  async getSessionFromStorage(): Promise<Session | null> {
    try {
      // Try to get session from Supabase's internal storage
      const {
        data: { session },
        error,
      } = await this.client.auth.getSession();

      if (error) {
        console.error("Error getting session from storage:", error);
        return null;
      }

      return session;
    } catch (error) {
      console.error("Failed to get session from storage:", error);
      return null;
    }
  }

  async refreshSession(): Promise<AuthResponse<Session>> {
    try {
      const {
        data: { session },
        error,
      } = await this.client.auth.refreshSession();

      if (error) {
        console.error("Session refresh failed:", error);
        this.recordOperation("refresh_session", false, error.message);
        return { data: null, error };
      }

      if (!session) {
        console.log("No session returned from refresh");
        this.recordOperation("refresh_session", false, "No session returned");
        return { data: null, error: new Error("No session returned") };
      }

      this.recordOperation("refresh_session", true);
      return { data: session, error: null };
    } catch (error) {
      const errorObj =
        error instanceof Error ? error : new Error(String(error));
      console.error("Session refresh exception:", errorObj);
      this.recordOperation("refresh_session", false, errorObj.message);
      return { data: null, error: errorObj };
    }
  }

  async validateSession(session: Session): Promise<boolean> {
    try {
      const expiresAt = new Date(session.expires_at || 0);
      const now = new Date();
      const bufferTime = 5 * 60 * 1000; // 5 minutes buffer

      return expiresAt.getTime() - now.getTime() > bufferTime;
    } catch (error) {
      return false;
    }
  }

  // Authentication
  async signIn(
    email: string,
    password: string,
  ): Promise<AuthResponse<Session>> {
    try {
      const { data, error } = await this.client.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        this.recordOperation("sign_in", false, error.message);

        // Check if it's an "invalid login credentials" error
        if (error.message === "Invalid login credentials") {
          // Don't check admin users - just throw invalid credentials
          const customError = new Error("invalid_credentials") as any;
          customError.custom = true;
          return { data: null, error: customError };
        }

        return { data: null, error };
      }

      this.recordOperation("sign_in", true);
      return { data: data.session, error: null };
    } catch (error) {
      const errorObj =
        error instanceof Error ? error : new Error(String(error));
      this.recordOperation("sign_in", false, errorObj.message);
      return { data: null, error: errorObj };
    }
  }

  async signInWithProvider(
    provider: "google" | "github" | "apple",
  ): Promise<void> {
    try {
      const { error } = await this.client.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: "offline",
            prompt: "consent",
          },
        },
      });

      if (error) throw error;
      this.recordOperation("sign_in_with_provider", true);
    } catch (error) {
      this.recordOperation("sign_in_with_provider", false, String(error));
      throw error;
    }
  }

  async signUp(
    email: string,
    password: string,
    metadata?: Record<string, any>,
  ): Promise<AuthResponse<{ success: boolean }>> {
    try {
      console.log("AuthService.signUp called for:", email);

      const { data, error } = await this.client.auth.signUp({
        email,
        password,
        options: {
          data: {
            ...metadata,
            full_name: metadata?.full_name || "",
          },
        },
      });

      if (error) {
        console.error("Supabase auth signUp error:", error);
        console.error("Error details:", {
          message: error.message,
          name: error.name,
          status: error.status,
        });
        this.recordOperation("sign_up", false, error.message);
        return { data: null, error };
      }

      console.log("Supabase auth signUp success response:", {
        user: data.user,
        session: data.session,
      });

      // IMPORTANT: For now, just return success without trying to create profile
      // The profile should be created by Supabase triggers
      // If it's not working, check your database triggers in Supabase dashboard

      this.recordOperation("sign_up", true);
      return { data: { success: true }, error: null };
    } catch (error) {
      console.error("Unexpected error in signUp:", error);
      const errorObj =
        error instanceof Error ? error : new Error(String(error));
      this.recordOperation("sign_up", false, errorObj.message);
      return { data: null, error: errorObj };
    }
  }

  async signOut(): Promise<AuthResponse<{ success: boolean }>> {
    try {
      const { error } = await this.client.auth.signOut();

      if (error) {
        this.recordOperation("sign_out", false, error.message);
        return { data: null, error };
      }

      this.recordOperation("sign_out", true);
      return { data: { success: true }, error: null };
    } catch (error) {
      const errorObj =
        error instanceof Error ? error : new Error(String(error));
      this.recordOperation("sign_out", false, errorObj.message);
      return { data: null, error: errorObj };
    }
  }

  // User Management
  async getUserProfile(userId: string): Promise<AuthResponse<UserSession>> {
    try {
      // FIRST: Get the basic profile
      const { data: profile, error } = await this.client
        .from("profiles")
        .select("*")
        .or(`uid.eq.${userId},user_id.eq.${userId}`)
        .maybeSingle();

      if (error) {
        this.recordOperation("get_user_profile", false, error.message);
        return { data: null, error };
      }

      if (!profile) {
        console.log("No profile found, creating minimal user");
        const { data: userData } = await this.client.auth.getUser();

        // CRITICAL FIX: For minimal user, we don't have a profile ID yet
        // So uid should be null or undefined, not set to auth UID
        const minimalUser: UserSession = {
          id: userId, // ← Auth UID
          email: userData.user?.email || "",
          display_name: null,
          avatar_url: null,
          is_verified: false,
          role: [],
          created_at: userData.user?.created_at || new Date().toISOString(),
          full_name: null,
          // uid: userId, // ← REMOVE THIS LINE - don't set uid to auth UID
          // Or set it to null explicitly
        };

        this.recordOperation("get_user_profile", true);
        return { data: minimalUser, error: null };
      }

      // SECOND: Get related data in parallel
      const [
        { data: talentProfile },
        { data: businesses },
        { data: userSettings },
      ] = await Promise.all([
        this.client
          .from("talent_profiles")
          .select("*")
          .eq("user_id", userId)
          .maybeSingle(),
        this.client.from("businesses").select("*").eq("owner_id", userId),
        this.client
          .from("user_settings")
          .select("*")
          .eq("user_id", userId)
          .maybeSingle(),
      ]);

      // CRITICAL FIX: Properly map the IDs
      const userSession: UserSession = {
        // id should be the auth UID (from Supabase Auth)
        id: userId,

        // uid should be the profile's database ID (from profiles table)
        uid: profile.id, // ← Use profile.id, NOT profile.uid and NOT userId

        // Or if your profile has a uid field that's the same as auth UID:
        // uid: profile.uid, // ← This should be the profile's uid field

        email: profile.email,
        display_name: profile.full_name || null,
        avatar_url: profile.avatar_url || null,
        is_verified: profile.is_verified || false,
        role: profile.role || [],
        created_at: profile.created_at,
        full_name: profile.full_name || null,

        // Add extended fields
        bio: profile.bio || null,
        location: profile.location || null,
        website: profile.website || null,
        church_affiliation: profile.church_affiliation || null,
        denomination: profile.denomination || null,
        life_group: profile.life_group || null,
        primary_craft: profile.primary_craft || null,
        secondary_crafts: profile.secondary_crafts || null,
        skill_set: profile.skill_set || null,
        economic_intent: profile.economic_intent || null,
        faith_alignment: profile.faith_alignment || null,
        profile_setup_completed: profile.profile_setup_completed || null,
        setup_stage: profile.setup_stage || null,

        // Add related data as separate properties if needed
        ...(talentProfile && { talent_profile: talentProfile }),
        ...(businesses && { businesses }),
        ...(userSettings && { settings: userSettings }),
      };

      this.recordOperation("get_user_profile", true);
      return { data: userSession, error: null };
    } catch (error) {
      console.error("Error in getUserProfile:", error);
      const errorObj =
        error instanceof Error ? error : new Error(String(error));
      this.recordOperation("get_user_profile", false, errorObj.message);
      return { data: null, error: errorObj };
    }
  }

  async updateProfile(
    data: Partial<UserSession>,
  ): Promise<AuthResponse<UserSession>> {
    try {
      const { data: userData } = await this.client.auth.getUser();
      if (!userData.user) {
        throw new Error("No authenticated user");
      }

      const { error } = await this.client
        .from("profiles")
        .update({
          full_name: data.full_name || data.display_name,
          avatar_url: data.avatar_url,
          updated_at: new Date().toISOString(),
        })
        .eq("id", userData.user.id);

      if (error) {
        this.recordOperation("update_profile", false, error.message);
        return { data: null, error };
      }

      // Get updated profile
      const response = await this.getUserProfile(userData.user.id);

      if (response.error) {
        this.recordOperation("update_profile", false, response.error.message);
        return response;
      }

      this.recordOperation("update_profile", true);
      return response;
    } catch (error) {
      const errorObj =
        error instanceof Error ? error : new Error(String(error));
      this.recordOperation("update_profile", false, errorObj.message);
      return { data: null, error: errorObj };
    }
  }

  // Password Management
  async resetPassword(
    email: string,
  ): Promise<AuthResponse<{ success: boolean }>> {
    try {
      // Personalize the email with your brand name
      const { error } = await this.client.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });

      if (error) {
        this.recordOperation("reset_password", false, error.message);
        return { data: null, error };
      }

      this.recordOperation("reset_password", true);
      return { data: { success: true }, error: null };
    } catch (error) {
      const errorObj =
        error instanceof Error ? error : new Error(String(error));
      this.recordOperation("reset_password", false, errorObj.message);
      return { data: null, error: errorObj };
    }
  }

  async updatePassword(
    newPassword: string,
  ): Promise<AuthResponse<{ success: boolean }>> {
    try {
      const { error } = await this.client.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        this.recordOperation("update_password", false, error.message);
        return { data: null, error };
      }

      this.recordOperation("update_password", true);
      return { data: { success: true }, error: null };
    } catch (error) {
      const errorObj =
        error instanceof Error ? error : new Error(String(error));
      this.recordOperation("update_password", false, errorObj.message);
      return { data: null, error: errorObj };
    }
  }

  // Email Verification
  async verifyEmail(): Promise<AuthResponse<{ success: boolean }>> {
    try {
      const { data: userData } = await this.client.auth.getUser();
      const email = userData.user?.email || "";

      const { error } = await this.client.auth.resend({
        type: "signup",
        email: email,
      });

      if (error) {
        this.recordOperation("verify_email", false, error.message);
        return { data: null, error };
      }

      this.recordOperation("verify_email", true);
      return { data: { success: true }, error: null };
    } catch (error) {
      const errorObj =
        error instanceof Error ? error : new Error(String(error));
      this.recordOperation("verify_email", false, errorObj.message);
      return { data: null, error: errorObj };
    }
  }

  // Auth State Listener
  onAuthStateChange(
    callback: (event: string, session: Session | null) => void,
  ) {
    const {
      data: { subscription },
    } = this.client.auth.onAuthStateChange((event, session) => {
      this.recordOperation("auth_state_change", true, event);
      callback(event, session);
    });

    return { subscription };
  }

  // Analytics and Monitoring
  private recordOperation(operation: string, success: boolean, error?: string) {
    const operationRecord: AuthOperation = {
      operation,
      duration: 0, // Would calculate actual duration
      success,
      error,
      timestamp: new Date(),
    };

    this.operations.push(operationRecord);

    // Keep only recent operations
    if (this.operations.length > this.MAX_OPERATIONS) {
      this.operations = this.operations.slice(-this.MAX_OPERATIONS);
    }

    // Emit to analytics service if needed
    this.emitMetrics(operationRecord);
  }

  private emitMetrics(operation: AuthOperation) {
    // Integrate with your analytics/monitoring service
    // Example: Sentry, DataDog, etc.
    if (typeof window !== "undefined" && (window as any).gtag) {
      (window as any).gtag("event", "auth_operation", {
        event_category: "authentication",
        event_label: operation.operation,
        value: operation.success ? 1 : 0,
        error_message: operation.error,
      });
    }
  }

  // Get operation metrics (for debugging/monitoring)
  getMetrics() {
    return {
      totalOperations: this.operations.length,
      successRate:
        this.operations.filter((op) => op.success).length /
        this.operations.length,
      recentOperations: this.operations.slice(-10),
      errors: this.operations.filter((op) => !op.success),
    };
  }
}
